import { useState } from "react"
import { Activity } from "lucide-react"
import { Link } from "react-router-dom"
import { useClientTheme } from "@/app/components/ThemeContext"
import { PageHeader } from "@/app/components/PageHeader"
import { LoadingState } from "@/app/components/LoadingState"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { GridMark } from "@/app/components/GridMark"
import { SelectFilter } from "@/app/components/FilterBar/SelectFilter"
import { secondaryButton } from "@/app/lib/buttonStyles"
import { useRecentUpdates } from "./useRecentUpdates"
import { groupByDay } from "./feedTransforms"
import { verb, noun, recordPhrase, fieldLabel, actorName, initials } from "./phrasing"
import type { FeedAction, FeedActor, FeedEntry } from "./types"

const ACTION_OPTIONS = [
  { label: "Created", value: "create" },
  { label: "Edited", value: "update" },
  { label: "Deleted", value: "delete" },
  { label: "Restored", value: "restore" },
  { label: "Imported", value: "import" },
]

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

export function RecentUpdatesPage() {
  const { theme } = useClientTheme()
  const [userId, setUserId] = useState<string | undefined>()
  const [action, setAction] = useState<FeedAction | undefined>()

  const q = useRecentUpdates({ userId, action })

  const entries = q.data?.pages.flatMap((p) => p.data) ?? []
  const groups = groupByDay(entries)
  const actors = q.data?.pages[0]?.meta.actors ?? []
  const totalCount = q.data?.pages[0]?.meta.total_count ?? 0

  const avatarBase = {
    width: "28px",
    height: "28px",
    borderRadius: "9999px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontFamily: "var(--font-ui)",
    fontSize: "11px",
    fontWeight: 600,
    backgroundColor: theme.bgSurface2,
    color: theme.textPrimary,
    border: `1px solid ${theme.borderColor}`,
  } as const

  const timeStyle = {
    fontFamily: "var(--font-data)",
    fontSize: "12px",
    color: theme.textDim,
    flexShrink: 0,
    whiteSpace: "nowrap" as const,
  }

  const nameStyle = {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    fontWeight: 600,
    color: theme.textPrimary,
  }

  const phraseStyle = {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    color: theme.textMuted,
  }

  // Actor name plus the LandTrack staff mark — shared by single-entry and
  // import-run rows. The `superadmin` flag is the staff signal now.
  const renderActorName = (actor: FeedActor | null) => (
    <>
      <span style={nameStyle}>{actorName(actor)}</span>
      {actor?.superadmin && (
        <span
          role="img"
          aria-label="LandTrack staff"
          title="LandTrack"
          style={{ display: "inline-flex", verticalAlign: "-2px", marginLeft: "5px" }}
        >
          <GridMark size={13} />
        </span>
      )}
    </>
  )

  const renderSingle = (entry: FeedEntry) => {
    const { record_label } = entry
    return (
      <>
        {renderActorName(entry.actor)} <span style={phraseStyle}>{verb(entry.action)} </span>
        {record_label !== null && entry.record_type === "MineralTransaction" ? (
          <Link
            to={`/transactions?tx=${encodeURIComponent(record_label)}`}
            style={{
              fontFamily: "var(--font-data)",
              fontSize: "13px",
              fontWeight: 500,
              color: theme.brandAccent,
              textDecoration: "underline",
              textUnderlineOffset: "2px",
            }}
          >
            {record_label}
          </Link>
        ) : (
          <span style={phraseStyle}>{recordPhrase(entry.record_type, entry.record_label)}</span>
        )}
        {entry.changed_fields && entry.changed_fields.length > 0 && (
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              color: theme.textDim,
            }}
          >
            {" · "}
            {entry.changed_fields.map(fieldLabel).join(", ")}
          </span>
        )}
      </>
    )
  }

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div className="max-w-[1400px]">
        <PageHeader
          title="Recent Updates"
          subtitle="Activity across transactions, wells, and notes"
          theme={theme}
          icon={<Activity size={17} strokeWidth={1.75} />}
        />

        <div
          className="rounded-lg overflow-hidden"
          style={{
            backgroundColor: theme.bgSurface,
            border: `1px solid ${theme.borderColor}`,
          }}
        >
          <FilterBar
            hasFilters={!!(userId || action)}
            onClear={() => {
              setUserId(undefined)
              setAction(undefined)
            }}
            resultCount={entries.length}
            totalCount={totalCount}
            entityLabel="entry"
            theme={theme}
          >
            <SelectFilter
              value={userId ?? ""}
              onChange={(v) => setUserId(v || undefined)}
              placeholder="All people"
              options={actors.map((a) => ({ label: a.name || a.email, value: a.id }))}
              theme={theme}
            />
            <SelectFilter
              value={action ?? ""}
              onChange={(v) => setAction((v as FeedAction) || undefined)}
              placeholder="All actions"
              options={ACTION_OPTIONS}
              theme={theme}
            />
          </FilterBar>

          {q.isLoading ? (
            <LoadingState theme={theme} />
          ) : q.isError ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                fontFamily: "var(--font-ui)",
              }}
            >
              <div style={{ fontSize: "14px", fontWeight: 500, color: theme.textPrimary }}>
                Couldn’t load recent activity
              </div>
              <div style={{ fontSize: "13px", color: theme.textMuted, marginTop: "4px" }}>
                Something went wrong while fetching the feed.
              </div>
              <button
                type="button"
                onClick={() => q.refetch()}
                className="btn mt-4 px-4 rounded-lg"
                style={{
                  height: "34px",
                  fontFamily: "var(--font-ui)",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: theme.brandAccent,
                  backgroundColor: `${theme.brandAccent}12`,
                  border: `1px solid ${theme.brandAccent}30`,
                }}
              >
                Retry
              </button>
            </div>
          ) : entries.length === 0 ? (
            <div
              style={{
                padding: "64px 24px",
                textAlign: "center",
                fontFamily: "var(--font-ui)",
                fontSize: "14px",
                color: theme.textMuted,
              }}
            >
              {userId || action ? "No activity matches these filters" : "No recent activity"}
            </div>
          ) : (
            <div>
              {groups.map((group) => (
                <div key={group.key}>
                  <div
                    className="px-5 py-2"
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: "11px",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      color: theme.textDim,
                      backgroundColor: theme.bgSurface2,
                      borderTop: `1px solid ${theme.borderSubtle}`,
                      borderBottom: `1px solid ${theme.borderSubtle}`,
                    }}
                  >
                    {group.label}
                  </div>

                  {group.entries.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 px-5 py-3"
                      style={{ borderBottom: `1px solid ${theme.borderSubtle}` }}
                    >
                      <div style={avatarBase}>{initials(item.actor)}</div>
                      <div className="flex-1 min-w-0">
                        {item.record_type === "ImportRun" ? (
                          <>
                            {renderActorName(item.actor)}{" "}
                            <span style={phraseStyle}>
                              imported {item.import?.count ?? 0}{" "}
                              {noun(item.import?.target_type ?? "record", item.import?.count ?? 0)}
                            </span>
                          </>
                        ) : (
                          renderSingle(item)
                        )}
                      </div>
                      <span style={timeStyle}>{formatTime(item.created_at)}</span>
                    </div>
                  ))}
                </div>
              ))}

              {q.hasNextPage && (
                <div className="flex justify-center px-5 py-4">
                  <button
                    type="button"
                    onClick={() => q.fetchNextPage()}
                    disabled={q.isFetchingNextPage || q.isPlaceholderData}
                    className="btn"
                    style={{
                      ...secondaryButton(theme),
                      color: theme.textPrimary,
                      opacity: q.isFetchingNextPage ? 0.6 : 1,
                    }}
                  >
                    {q.isFetchingNextPage ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
